import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';

export function useCatalogUserLookup(options?: { namespace?: string }) {
  const catalogApi = useApi(catalogApiRef);
  const namespace = options?.namespace ?? 'default';

  function isEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  }

  async function findUserByUserId(id: string): Promise<Entity | undefined> {
    const trimmed = (id ?? '').trim();
    if (trimmed.length === 0) return undefined;

    let ref: string;
    try {
      const parsed = parseEntityRef(trimmed, {
        defaultKind: 'User',
        defaultNamespace: namespace,
      });
      ref = stringifyEntityRef(parsed);
    } catch {
      ref = `user:${namespace}/${trimmed}`;
    }

    const entity = await catalogApi.getEntityByRef(ref).catch(() => undefined);
    if (entity?.kind?.toLowerCase() === 'user') return entity;
    return undefined;
  }

  async function findUserByEmail(email: string): Promise<Entity | undefined> {
    const target = (email ?? '').trim().toLowerCase();
    if (target.length === 0) return undefined;

    try {
      const res = await catalogApi.getEntities({
        filter: { kind: ['User'], 'spec.profile.email': [target] },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'spec.profile.email',
          'spec.profile.displayName',
        ],
      });
      return res.items.find(
        e => (e as any)?.spec?.profile?.email?.toLowerCase() === target,
      );
    } catch {
      return undefined;
    }
  }

  return {
    findUserByUserId,
    findUserByEmail,
    isEmail,
  };
}
